"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { motion, useInView } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import useAutoRotate from "@/hooks/useAutoRotate";
import RichText from "./RichText";

/* Number and illustration per step — presentation, index-aligned with
   `howItWorks.steps` in the dictionary. */
const STEP_VISUALS = [
  { num: "01", img: "/images/step1_blue.png" },
  { num: "02", img: "/images/step2_blue.png" },
  { num: "03", img: "/images/step3_blue.png" },
];

type Step = { title: string; desc: string; items: string[] };

export default function HowItWorks() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-20px" });
  const { t, tv } = useLanguage();

  const steps = tv<Step[]>("howItWorks.steps").map((s, i) => ({ ...s, ...STEP_VISUALS[i] }));

  /* Auto-cycle through the steps. `isInView` above is deliberately
     `once: true` — it drives the entrance animations, which should not replay
     on every scroll past — so it was the wrong signal to gate a timer on: it
     latches true the first time the section is seen and never goes back, and
     the interval then ran for the rest of the visit. `useAutoRotate` tracks
     visibility properly (both directions), stops in a background tab, and
     holds still under `prefers-reduced-motion`. */
  const {
    containerRef: rotateRef,
    index: activeIndex,
    setIndex: setActiveIndex,
    pause,
    resume,
    paused: isPaused,
  } = useAutoRotate(STEP_VISUALS.length, 3500);
  const setIsPaused = (v: boolean) => (v ? pause() : resume());

  /* Two hooks want a handle on the same <section> — `useInView` for the
     one-shot entrance and `useAutoRotate` for the live visibility gate — so
     one callback ref feeds both. */
  const setSectionRef = (node: HTMLElement | null) => {
    (ref as React.MutableRefObject<HTMLElement | null>).current = node;
    rotateRef.current = node;
  };

  return (
    <section ref={setSectionRef} id="how-it-works" className="w-full scroll-mt-24 py-12 sm:py-16 md:py-20 bg-white relative overflow-hidden">
      <div className="site-container relative z-10">

        {/* Top-right Decorative Dot Matrix Grid */}
        <div className="absolute top-0 right-3 sm:right-6 md:right-8 lg:right-10 pointer-events-none opacity-20 sm:opacity-30">
          <svg width="90" height="60" viewBox="0 0 100 65" fill="none" className="sm:w-[100px] sm:h-[65px]">
            <pattern id="dot-grid" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
              <circle cx="3" cy="3" r="1.8" fill="#64748B" opacity="0.3" />
            </pattern>
            <rect width="100" height="65" fill="url(#dot-grid)" />
          </svg>
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center text-center gap-2 pt-2 mb-8 sm:mb-10 md:mb-12"
        >
          <h2 className="font-heading section-heading text-[#0d1117]">
            <RichText text={t("howItWorks.heading")} />
          </h2>
          <p className="text-[13px] sm:text-[14px] md:text-[15px] text-[#64748B] font-medium max-w-[540px] leading-relaxed">
            {t("howItWorks.body")}
          </p>
        </motion.div>

        {/* Tablet & Desktop Horizontal View (md: 768px and up) */}
        <div className="hidden md:block relative">
          
          {/* Subtle Clean Wave Connector Line */}
          <div className="relative w-full h-[120px] lg:h-[140px] flex items-center justify-center mb-4">
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none z-0 overflow-visible"
              viewBox="0 0 1000 160"
              preserveAspectRatio="none"
            >
              {/* Subtle Neutral Gray Wave Ribbon */}
              <motion.path
                initial={{ pathLength: 0, opacity: 0 }}
                animate={isInView ? { pathLength: 1, opacity: 1 } : {}}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                d="M 100,80 C 100,40 125,25 166.6,25 C 208,25 280,65 333.3,65 C 386.6,65 458,25 500,25 C 542,25 613,65 666.6,65 C 720,65 791,25 833.3,25 C 875,25 900,40 900,80 C 900,120 875,135 833.3,135 C 791,135 720,95 666.6,95 C 613,95 542,135 500,135 C 458,135 386.6,95 333.3,95 C 280,95 208,135 166.6,135 C 125,135 100,120 100,80 Z"
                fill="none"
                stroke="#E2E8F0"
                strokeWidth="2"
              />

              {/* Animated Emerald Dashed Pulse Line */}
              <motion.path
                d="M 100,80 C 100,40 125,25 166.6,25 C 208,25 280,65 333.3,65 C 386.6,65 458,25 500,25 C 542,25 613,65 666.6,65 C 720,65 791,25 833.3,25 C 875,25 900,40 900,80"
                fill="none"
                stroke="#294ADE"
                strokeWidth="2"
                strokeDasharray="6,10"
                strokeLinecap="round"
                animate={{ strokeDashoffset: [0, -96] }}
                transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                opacity={0.6}
              />
            </svg>

            {/* Circles positioned along the wave */}
            <div className="w-full grid grid-cols-3 relative z-10">
              {steps.map((step, i) => {
                const isActive = activeIndex === i;
                return (
                  <div
                    key={i}
                    className="flex flex-col items-center justify-center cursor-pointer"
                    onMouseEnter={() => {
                      setActiveIndex(i);
                      setIsPaused(true);
                    }}
                    onMouseLeave={() => setIsPaused(false)}
                  >
                    {/* Circle Image Container */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={isInView ? { opacity: 1, scale: isActive ? 1.1 : 1, y: isActive ? -4 : [0, -2, 0] } : {}}
                      transition={{
                        scale: { duration: 0.3 },
                        y: isActive ? { duration: 0.3 } : { duration: 4, repeat: Infinity, ease: "easeInOut", delay: i * 0.8 }
                      }}
                      className="relative group"
                    >
                      {/* Main White Circle holding Real Image Illustration */}
                      <motion.div
                        animate={{
                          borderColor: isActive ? "#294ADE" : "#E2E8F0",
                          boxShadow: isActive
                            ? "0 10px 25px rgba(41,74,222,0.18)"
                            : "0 4px 12px rgba(0,0,0,0.04)",
                        }}
                        transition={{ duration: 0.3 }}
                        className="w-18 h-18 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-white border-2 flex items-center justify-center relative z-10 overflow-hidden p-1.5 shadow-sm"
                      >
                        {/* next/image, not a raw <img>. These three source
                            PNGs are 723KB, 1.08MB and 1.13MB — 2.9MB in total
                            — and they render here inside a 96px circle (and
                            at 48px in the mobile list below), so every byte
                            past about 200px square was pure waste on a phone.
                            `sizes` caps what the optimizer generates at the
                            largest box either layout actually uses. The scale
                            hover stays on the wrapper so the element being
                            animated is still a plain transform. */}
                        <motion.div
                          animate={{ scale: isActive ? 1.08 : 1 }}
                          transition={{ duration: 0.3 }}
                          className="relative w-full h-full"
                        >
                          <Image
                            src={step.img}
                            alt={step.title}
                            fill
                            sizes="96px"
                            className="object-contain"
                          />
                        </motion.div>
                      </motion.div>
                    </motion.div>

                    {/* Step Number Badge under Circle */}
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.3 + i * 0.15 }}
                      className="mt-2 text-center"
                    >
                      <motion.span
                        animate={{
                          backgroundColor: isActive ? "#294ADE" : "#F1F5F9",
                          color: isActive ? "#FFFFFF" : "#64748B",
                          scale: isActive ? 1.08 : 1,
                        }}
                        transition={{ duration: 0.25 }}
                        // No transition-colors here: framer-motion already
                        // tweens backgroundColor/color over 0.25s by writing
                        // the inline style every frame. A CSS colour transition
                        // on top of that spawned a fresh 150ms transition on
                        // every one of those frames (Lighthouse listed them as
                        // non-composited "color"/"background-color" animations)
                        // and only lagged the tween. The tween itself is the
                        // visible effect and is unchanged.
                        className="inline-block font-bold text-[11.5px] lg:text-[12.5px] px-2.5 sm:px-3 py-0.5 rounded-full tracking-wider border border-slate-200/60"
                      >
                        {step.num}
                      </motion.span>
                    </motion.div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Clean Premium 3 Cards Grid with Auto-Moving Highlight */}
          <div className="grid grid-cols-3 gap-3 md:gap-4 lg:gap-6 mt-4">
            {steps.map((step, i) => {
              const isActive = activeIndex === i;
              return (
                <div key={i} className="relative">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.3 + i * 0.12 }}
                    onMouseEnter={() => {
                      setActiveIndex(i);
                      setIsPaused(true);
                    }}
                    onMouseLeave={() => setIsPaused(false)}
                    className={`relative flex flex-col items-center text-center p-4 sm:p-5 lg:p-6 rounded-[20px] md:rounded-[24px] transition-all duration-400 cursor-pointer overflow-hidden z-10 h-full bg-white ${
                      isActive
                        ? "border-2 border-[#294ADE] shadow-[0_12px_32px_rgba(0,0,0,0.08)] -translate-y-2"
                        : "border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.02)] opacity-85 hover:opacity-100 hover:border-slate-300 hover:shadow-md"
                    }`}
                  >
                    {/* Top Progress Timer Bar for Active Auto-Moving Card */}
                    {isActive && (
                      <motion.div
                        key={`progress-${i}-${activeIndex}`}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: isPaused ? 0 : 3.5, ease: "linear" }}
                        style={{ transformOrigin: "left" }}
                        className="absolute top-0 left-0 right-0 h-1 bg-[#294ADE] z-20"
                      />
                    )}

                    {/* Step Header Badge */}
                    <div className="flex items-center gap-2 mb-1.5 mt-0.5">
                      <span
                        className={`text-[10px] sm:text-[11px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full transition-colors duration-300 ${
                          isActive
                            ? "bg-[#294ADE] text-white"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {t("howItWorks.stepLabel")} {step.num}
                      </span>
                    </div>

                    <h3
                      className={`font-bold text-[16px] sm:text-[17px] lg:text-[19px] mb-1.5 leading-tight transition-colors duration-300 ${
                        isActive
                          ? "text-[#0F172A]"
                          : "text-[#1E293B]"
                      }`}
                    >
                      {step.title}
                    </h3>
                    <p className="text-[12px] sm:text-[12.5px] lg:text-[13px] text-[#64748B] leading-relaxed mb-3.5 max-w-[280px] font-normal flex-1">
                      {step.desc}
                    </p>

                    <div className="w-full h-px bg-slate-100 mb-3.5" />

                    {/* Bullets List Cards */}
                    <ul className="flex flex-col gap-1.5 sm:gap-2 text-left w-full">
                      {step.items.map((item, j) => (
                        <motion.li
                          key={j}
                          whileHover={{ x: 3 }}
                          className={`p-1.5 px-2.5 sm:p-2 sm:px-3 rounded-lg flex items-center gap-2 transition-all duration-200 border ${
                            isActive
                              ? "bg-slate-50/80 border-slate-200/80"
                              : "bg-slate-50/50 border-slate-100"
                          }`}
                        >
                          <div
                            className={`w-4 h-4 sm:w-5 sm:h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                              isActive
                                ? "bg-[#294ADE] text-white"
                                : "bg-slate-200/70 text-slate-500"
                            }`}
                          >
                            <CheckCircle2
                              size={11}
                              className={isActive ? "text-white" : "text-slate-600"}
                            />
                          </div>
                          <span
                            className={`text-[11.5px] sm:text-[12px] lg:text-[12.5px] font-medium leading-tight transition-colors duration-200 ${
                              isActive
                                ? "text-[#0F172A]"
                                : "text-[#475569]"
                            }`}
                          >
                            {item}
                          </span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile View for Phones (< 768px) */}
        <div className="block md:hidden flex flex-col gap-4">
          {steps.map((step, i) => {
            const isActive = activeIndex === i;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.1 }}
                onClick={() => {
                  setActiveIndex(i);
                  setIsPaused(true);
                }}
                className={`flex gap-3.5 sm:gap-4 relative p-4 sm:p-5 rounded-xl sm:rounded-2xl transition-all duration-300 bg-white ${
                  isActive
                    ? "border-2 border-[#294ADE] shadow-md"
                    : "border border-slate-200 shadow-xs opacity-90"
                }`}
              >
                {/* Vertical Line Connector on Mobile */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <motion.div
                    whileTap={{ scale: 0.95 }}
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white shadow-xs border border-slate-200 p-1 flex items-center justify-center relative z-10 overflow-hidden"
                  >
                    {/* Same 2.9MB-of-PNG problem as the desktop circle above,
                        and worse here: this is the box mobile actually gets,
                        and it is 48px across. */}
                    <Image src={step.img} alt={step.title} width={48} height={48} sizes="48px" className="w-full h-full object-contain" />
                  </motion.div>
                  <span
                    className={`font-bold text-[11px] sm:text-[12px] mt-1.5 px-2 py-0.5 rounded-full transition-colors ${
                      isActive ? "bg-[#294ADE] text-white" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {step.num}
                  </span>
                </div>

                {/* Right Content */}
                <div className="flex-1 pt-0.5">
                  <h3 className="font-bold text-[15px] sm:text-[16px] text-[#0F172A] mb-1 leading-snug">
                    {step.title}
                  </h3>
                  <p className="text-[12px] sm:text-[13px] text-[#64748B] leading-relaxed mb-2.5">
                    {step.desc}
                  </p>
                  <ul className="flex flex-col gap-1.5">
                    {step.items.map((item, j) => (
                      <li key={j} className="flex items-center gap-2">
                        <div
                          className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-md flex items-center justify-center flex-shrink-0 ${
                            isActive ? "bg-[#294ADE] text-white" : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          <CheckCircle2 size={9} className={isActive ? "text-white" : "text-slate-600"} />
                        </div>
                        <span className={`text-[12.5px] ${isActive ? "font-semibold text-[#0F172A]" : "text-[#475569]"}`}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}







